import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, X, HelpCircle, TrendingDown, Calendar, DollarSign,
  Zap, CheckCircle, Download, ArrowRight, Trophy, Target,
  Snowflake, Mountain, ChevronDown, ChevronUp, Sparkles,
  CreditCard, Car, GraduationCap, Home, Receipt, MoreHorizontal,
  Info, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Debt type options with icons
const DEBT_TYPES = [
  { value: "credit_card", label: "Credit Card", icon: CreditCard },
  { value: "auto_loan", label: "Auto Loan", icon: Car },
  { value: "student_loan", label: "Student Loan", icon: GraduationCap },
  { value: "personal_loan", label: "Personal Loan", icon: Receipt },
  { value: "line_of_credit", label: "Line of Credit", icon: CreditCard },
  { value: "medical", label: "Medical Debt", icon: Home },
  { value: "other", label: "Other", icon: MoreHorizontal },
];

// Colors for charts
const CHART_COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1"];

// Initial empty debt
const createEmptyDebt = (id) => ({
  id,
  name: "",
  balance: 0,
  interestRate: 19.99,
  minimumPayment: 0,
  type: "credit_card"
});

const MultiDebtCalculator = () => {
  // State for debts
  const [debts, setDebts] = useState([
    { ...createEmptyDebt(1), name: "Credit Card 1", balance: 5000, interestRate: 19.99, minimumPayment: 150 },
    { ...createEmptyDebt(2), name: "Student Loan", balance: 15000, interestRate: 5.5, minimumPayment: 200, type: "student_loan" },
  ]);
  const [extraPayment, setExtraPayment] = useState(200);
  const [oneTimePayment, setOneTimePayment] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState("avalanche");
  const [expandedSections, setExpandedSections] = useState({});

  // Add debt
  const addDebt = () => {
    if (debts.length >= 10) {
      toast.error("Maximum 10 debts allowed");
      return;
    }
    const newId = Math.max(...debts.map(d => d.id), 0) + 1;
    setDebts([...debts, createEmptyDebt(newId)]);
  };

  // Remove debt
  const removeDebt = (id) => {
    if (debts.length <= 1) {
      toast.error("You need at least one debt");
      return;
    }
    setDebts(debts.filter(d => d.id !== id));
  };

  // Update debt
  const updateDebt = (id, field, value) => {
    setDebts(debts.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  // Calculate totals
  const totals = useMemo(() => {
    const totalDebt = debts.reduce((sum, d) => sum + (d.balance || 0), 0);
    const totalMinPayment = debts.reduce((sum, d) => sum + (d.minimumPayment || 0), 0);
    const avgInterest = debts.length > 0 
      ? debts.reduce((sum, d) => sum + (d.interestRate || 0), 0) / debts.length 
      : 0;
    return { totalDebt, totalMinPayment, avgInterest };
  }, [debts]);

  // Calculate payoff for a specific strategy
  const calculatePayoff = (strategy) => {
    const validDebts = debts.filter(d => d.balance > 0 && d.minimumPayment > 0);
    if (validDebts.length === 0) return null;

    // Sort debts based on strategy
    let sortedDebts;
    if (strategy === "snowball") {
      sortedDebts = [...validDebts].sort((a, b) => a.balance - b.balance);
    } else if (strategy === "avalanche") {
      sortedDebts = [...validDebts].sort((a, b) => b.interestRate - a.interestRate);
    } else {
      sortedDebts = [...validDebts]; // minimum payments only
    }

    // Calculate month by month
    const monthlyExtra = strategy === "minimum" ? 0 : extraPayment;
    let currentDebts = sortedDebts.map(d => ({
      ...d,
      currentBalance: d.balance,
      paidOff: false,
      paidOffMonth: null
    }));
    
    const data = [];
    const milestones = [];
    let month = 0;
    let totalInterestPaid = 0;
    let totalPaid = 0;
    const maxMonths = 600; // Cap at 50 years
    let firstDebtPaidMonth = null;

    while (currentDebts.some(d => d.currentBalance > 0) && month < maxMonths) {
      month++;
      let monthData = { month };
      let availableExtra = monthlyExtra;
      
      // Add one-time payment in month 1
      if (month === 1) {
        availableExtra += oneTimePayment;
      }

      // Calculate payments for each debt
      for (let i = 0; i < currentDebts.length; i++) {
        const debt = currentDebts[i];
        if (debt.currentBalance <= 0) continue;

        // Calculate interest
        const monthlyRate = debt.interestRate / 100 / 12;
        const interest = debt.currentBalance * monthlyRate;
        totalInterestPaid += interest;

        // Determine payment
        let payment = debt.minimumPayment;
        
        // Add extra payment to first non-paid debt (for snowball/avalanche)
        if (strategy !== "minimum" && !debt.paidOff && availableExtra > 0) {
          // Find if this is the target debt
          const unpaidDebts = currentDebts.filter(d => d.currentBalance > 0);
          if (unpaidDebts[0]?.id === debt.id) {
            payment += availableExtra;
            availableExtra = 0;
          }
        }

        // Apply payment
        const actualPayment = Math.min(payment, debt.currentBalance + interest);
        debt.currentBalance = Math.max(0, debt.currentBalance + interest - actualPayment);
        totalPaid += actualPayment;

        // Check if paid off
        if (debt.currentBalance <= 0 && !debt.paidOff) {
          debt.paidOff = true;
          debt.paidOffMonth = month;
          milestones.push({ month, debtName: debt.name, type: "paidOff" });
          
          // Roll over payment to next debt
          if (strategy !== "minimum") {
            const remainingPayment = actualPayment - (debt.balance * monthlyRate);
            if (remainingPayment > 0) {
              availableExtra += debt.minimumPayment;
            }
          }

          if (!firstDebtPaidMonth) firstDebtPaidMonth = month;
        }

        monthData[debt.name] = Math.round(debt.currentBalance);
      }

      // Total remaining balance
      monthData.totalBalance = currentDebts.reduce((sum, d) => sum + d.currentBalance, 0);
      
      // Store data points periodically
      if (month % 3 === 0 || month === 1 || currentDebts.every(d => d.currentBalance <= 0)) {
        data.push(monthData);
      }
    }

    // Check if payoff is possible
    if (month >= maxMonths) {
      return {
        months: Infinity,
        totalInterest: Infinity,
        totalPaid: Infinity,
        data: [],
        milestones: [],
        firstDebtPaidMonth: null,
        payoffOrder: [],
        impossible: true
      };
    }

    return {
      months: month,
      totalInterest: Math.round(totalInterestPaid),
      totalPaid: Math.round(totalPaid),
      data,
      milestones,
      firstDebtPaidMonth,
      payoffOrder: currentDebts.map(d => ({ name: d.name, paidOffMonth: d.paidOffMonth })),
      impossible: false
    };
  };

  // Calculate all strategies
  const results = useMemo(() => {
    if (!showResults) return null;
    
    return {
      minimum: calculatePayoff("minimum"),
      snowball: calculatePayoff("snowball"),
      avalanche: calculatePayoff("avalanche")
    };
  }, [debts, extraPayment, oneTimePayment, showResults]);

  // Format currency
  const formatCurrency = (value) => {
    if (value === Infinity) return "N/A";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Get debt-free date
  const getDebtFreeDate = (months) => {
    if (months === Infinity) return "Never";
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    return date.toLocaleDateString('en-CA', { month: 'short', year: 'numeric' });
  };

  // Get recommendation
  const getRecommendation = () => {
    if (!results) return null;
    
    const rateSpread = Math.max(...debts.map(d => d.interestRate)) - Math.min(...debts.map(d => d.interestRate));
    
    if (rateSpread > 5) {
      return {
        strategy: "avalanche",
        reason: "Your interest rates vary significantly. Avalanche saves you the most money!"
      };
    } else {
      return {
        strategy: "snowball",
        reason: "Your interest rates are similar. Snowball gives you quick wins for motivation!"
      };
    }
  };

  // Pie chart data for debt composition
  const pieData = debts.filter(d => d.balance > 0).map((d, i) => ({
    name: d.name || `Debt ${d.id}`,
    value: d.balance,
    color: CHART_COLORS[i % CHART_COLORS.length]
  }));

  // Comparison chart data
  const comparisonData = results ? [
    { name: "Minimum Only", months: results.minimum?.months || 0, interest: results.minimum?.totalInterest || 0, color: "#ef4444" },
    { name: "Avalanche", months: results.avalanche?.months || 0, interest: results.avalanche?.totalInterest || 0, color: "#10b981" },
    { name: "Snowball", months: results.snowball?.months || 0, interest: results.snowball?.totalInterest || 0, color: "#3b82f6" },
  ] : [];

  const recommendation = getRecommendation();
  const selectedResult = results?.[selectedStrategy];

  // Download PDF
  const handleDownloadPDF = async () => {
    const content = `
DEBT-FREE PLAN - Compoundr.
============================

YOUR DEBTS
----------
${debts.map(d => `${d.name}: ${formatCurrency(d.balance)} @ ${d.interestRate}% APR, ${formatCurrency(d.minimumPayment)}/month minimum`).join('\n')}

TOTALS
------
Total Debt: ${formatCurrency(totals.totalDebt)}
Total Monthly Minimums: ${formatCurrency(totals.totalMinPayment)}
Extra Monthly Payment: ${formatCurrency(extraPayment)}
${oneTimePayment > 0 ? `One-Time Payment: ${formatCurrency(oneTimePayment)}` : ''}

STRATEGY COMPARISON
-------------------
Minimum Payments Only:
  - Debt-Free Date: ${getDebtFreeDate(results?.minimum?.months)}
  - Total Interest: ${formatCurrency(results?.minimum?.totalInterest)}
  - Months: ${results?.minimum?.months}

Debt Avalanche (Highest Interest First):
  - Debt-Free Date: ${getDebtFreeDate(results?.avalanche?.months)}
  - Total Interest: ${formatCurrency(results?.avalanche?.totalInterest)}
  - Months: ${results?.avalanche?.months}
  - Savings vs Minimum: ${formatCurrency((results?.minimum?.totalInterest || 0) - (results?.avalanche?.totalInterest || 0))}

Debt Snowball (Smallest Balance First):
  - Debt-Free Date: ${getDebtFreeDate(results?.snowball?.months)}
  - Total Interest: ${formatCurrency(results?.snowball?.totalInterest)}
  - Months: ${results?.snowball?.months}
  - First Debt Paid Off: Month ${results?.snowball?.firstDebtPaidMonth}

RECOMMENDED: ${recommendation?.strategy?.toUpperCase()}
${recommendation?.reason}

Generated by Compoundr. - compoundr.ca
    `;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'debt-free-plan.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Your debt-free plan has been downloaded!");
  };

  return (
    <div className="space-y-6" data-testid="multi-debt-calculator">
      {/* Header */}
      <div className="text-center">
        <Badge className="bg-red-100 text-red-700 mb-3">Multi-Debt Calculator</Badge>
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
          Create Your Debt-Free Plan
        </h2>
        <p className="text-slate-600 max-w-xl mx-auto">
          Compare Snowball vs Avalanche strategies and see exactly when you'll be debt-free
        </p>
      </div>

      {/* Step 1: Debt Input */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <span className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
            Enter Your Debts
          </CardTitle>
          <p className="text-sm text-slate-500">Add up to 10 debts. We'll calculate the best payoff strategy.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {debts.map((debt, index) => {
            const DebtIcon = DEBT_TYPES.find(t => t.value === debt.type)?.icon || CreditCard;
            return (
              <motion.div
                key={debt.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-slate-50 rounded-xl space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <DebtIcon className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium text-navy-700">Debt {index + 1}</span>
                  </div>
                  {debts.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDebt(debt.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="text-xs">Debt Name</Label>
                    <Input
                      placeholder="e.g., Visa Card"
                      value={debt.name}
                      onChange={(e) => updateDebt(debt.id, "name", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Balance ($)</Label>
                    <Input
                      type="number"
                      placeholder="5000"
                      value={debt.balance || ""}
                      onChange={(e) => updateDebt(debt.id, "balance", parseFloat(e.target.value) || 0)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      APR (%)
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="w-3 h-3 text-slate-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs text-xs">Annual Percentage Rate. Credit cards: 19-25%, Student loans: 5-7%</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="19.99"
                      value={debt.interestRate || ""}
                      onChange={(e) => updateDebt(debt.id, "interestRate", parseFloat(e.target.value) || 0)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Min Payment ($)</Label>
                    <Input
                      type="number"
                      placeholder="150"
                      value={debt.minimumPayment || ""}
                      onChange={(e) => updateDebt(debt.id, "minimumPayment", parseFloat(e.target.value) || 0)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select
                      value={debt.type}
                      onValueChange={(value) => updateDebt(debt.id, "type", value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEBT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </motion.div>
            );
          })}

          <Button
            variant="outline"
            onClick={addDebt}
            className="w-full border-dashed"
            disabled={debts.length >= 10}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Debt
          </Button>

          {/* Running Totals */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-navy-700 rounded-xl text-white">
            <div className="text-center">
              <p className="text-xs text-slate-300">Total Debt</p>
              <p className="text-xl font-bold font-mono">{formatCurrency(totals.totalDebt)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-300">Monthly Minimums</p>
              <p className="text-xl font-bold font-mono">{formatCurrency(totals.totalMinPayment)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-300">Avg Interest</p>
              <p className="text-xl font-bold font-mono">{totals.avgInterest.toFixed(1)}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Extra Payment */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <span className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
            Extra Monthly Payment
          </CardTitle>
          <p className="text-sm text-slate-500">How much extra can you put toward debt each month?</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Extra Monthly Payment</Label>
              <span className="text-lg font-bold text-emerald-600">{formatCurrency(extraPayment)}</span>
            </div>
            <Slider
              value={[extraPayment]}
              onValueChange={(v) => setExtraPayment(v[0])}
              min={0}
              max={2000}
              step={25}
              className="mb-2"
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>$0</span>
              <span>$500</span>
              <span>$1,000</span>
              <span>$1,500</span>
              <span>$2,000</span>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {[50, 100, 200, 500].map(amount => (
              <Button
                key={amount}
                variant={extraPayment === amount ? "default" : "outline"}
                onClick={() => setExtraPayment(amount)}
                className={extraPayment === amount ? "bg-emerald-500" : ""}
              >
                ${amount}
              </Button>
            ))}
          </div>

          <div className="p-4 bg-amber-50 rounded-xl">
            <Label className="text-sm flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-amber-600" />
              One-Time Payment (Optional)
            </Label>
            <p className="text-xs text-slate-600 mb-2">Tax refund? Bonus? Add it here!</p>
            <Input
              type="number"
              placeholder="0"
              value={oneTimePayment || ""}
              onChange={(e) => setOneTimePayment(parseFloat(e.target.value) || 0)}
            />
          </div>

          {extraPayment === 0 && (
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              Even $25/month extra can make a big difference! Try adjusting the slider to see the impact.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Calculate Button */}
      <Button
        onClick={() => setShowResults(true)}
        className="w-full bg-emerald-500 hover:bg-emerald-600 py-6 text-lg"
        disabled={totals.totalDebt === 0 || totals.totalMinPayment === 0}
      >
        <Sparkles className="w-5 h-5 mr-2" />
        Calculate My Debt-Free Plan
      </Button>

      {/* Results Section */}
      <AnimatePresence>
        {showResults && results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Strategy Comparison */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-navy-700 to-navy-800 text-white">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Strategy Comparison
                </CardTitle>
                <p className="text-sm text-slate-300">Compare different debt payoff methods side-by-side</p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Minimum Payments */}
                  <div className={`p-4 rounded-xl border-2 ${selectedStrategy === "minimum" ? "border-red-500 bg-red-50" : "border-slate-200"}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-red-100 text-red-700">Minimum Only</Badge>
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    </div>
                    <p className="text-xs text-slate-500 mb-2">What NOT to do</p>
                    <p className="text-2xl font-bold text-red-600 mb-1">
                      {results.minimum?.impossible ? "Never" : `${results.minimum?.months} months`}
                    </p>
                    <p className="text-sm text-slate-600">
                      Interest: {formatCurrency(results.minimum?.totalInterest)}
                    </p>
                    {!results.minimum?.impossible && (
                      <p className="text-xs text-slate-500 mt-2">
                        Debt-free: {getDebtFreeDate(results.minimum?.months)}
                      </p>
                    )}
                  </div>

                  {/* Avalanche */}
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedStrategy === "avalanche" 
                        ? "border-emerald-500 bg-emerald-50 shadow-lg" 
                        : "border-slate-200 hover:border-emerald-300"
                    }`}
                    onClick={() => setSelectedStrategy("avalanche")}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-emerald-100 text-emerald-700">Avalanche</Badge>
                      <Mountain className="w-5 h-5 text-emerald-500" />
                    </div>
                    <p className="text-xs text-slate-500 mb-2">Saves most money</p>
                    <p className="text-2xl font-bold text-emerald-600 mb-1">
                      {results.avalanche?.impossible ? "N/A" : `${results.avalanche?.months} months`}
                    </p>
                    <p className="text-sm text-slate-600">
                      Interest: {formatCurrency(results.avalanche?.totalInterest)}
                    </p>
                    {!results.avalanche?.impossible && results.minimum && !results.minimum.impossible && (
                      <p className="text-xs text-emerald-600 mt-2 font-medium">
                        Saves {formatCurrency(results.minimum.totalInterest - results.avalanche.totalInterest)}!
                      </p>
                    )}
                    {recommendation?.strategy === "avalanche" && (
                      <Badge className="mt-2 bg-emerald-500 text-white">Recommended</Badge>
                    )}
                  </div>

                  {/* Snowball */}
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedStrategy === "snowball" 
                        ? "border-blue-500 bg-blue-50 shadow-lg" 
                        : "border-slate-200 hover:border-blue-300"
                    }`}
                    onClick={() => setSelectedStrategy("snowball")}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-blue-100 text-blue-700">Snowball</Badge>
                      <Snowflake className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-xs text-slate-500 mb-2">Quick wins for motivation</p>
                    <p className="text-2xl font-bold text-blue-600 mb-1">
                      {results.snowball?.impossible ? "N/A" : `${results.snowball?.months} months`}
                    </p>
                    <p className="text-sm text-slate-600">
                      Interest: {formatCurrency(results.snowball?.totalInterest)}
                    </p>
                    {results.snowball?.firstDebtPaidMonth && (
                      <p className="text-xs text-blue-600 mt-2 font-medium">
                        First win in {results.snowball.firstDebtPaidMonth} months!
                      </p>
                    )}
                    {recommendation?.strategy === "snowball" && (
                      <Badge className="mt-2 bg-blue-500 text-white">Recommended</Badge>
                    )}
                  </div>
                </div>

                {/* Recommendation */}
                {recommendation && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-navy-700">Our Recommendation</p>
                        <p className="text-sm text-slate-600">{recommendation.reason}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Key Metrics */}
            {selectedResult && !selectedResult.impossible && (
              <div className="grid sm:grid-cols-4 gap-4">
                <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                  <CardContent className="p-4 text-center">
                    <Calendar className="w-6 h-6 mx-auto mb-2 opacity-80" />
                    <p className="text-xs opacity-80">Debt-Free Date</p>
                    <p className="text-xl font-bold">{getDebtFreeDate(selectedResult.months)}</p>
                    <p className="text-xs opacity-80">{selectedResult.months} months</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                    <p className="text-xs text-slate-500">Total Interest</p>
                    <p className="text-xl font-bold text-amber-600">{formatCurrency(selectedResult.totalInterest)}</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <Trophy className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                    <p className="text-xs text-slate-500">First Win</p>
                    <p className="text-xl font-bold text-blue-600">
                      {selectedResult.firstDebtPaidMonth ? `Month ${selectedResult.firstDebtPaidMonth}` : "N/A"}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                    <p className="text-xs text-slate-500">Interest Saved</p>
                    <p className="text-xl font-bold text-emerald-600">
                      {results.minimum && !results.minimum.impossible 
                        ? formatCurrency(results.minimum.totalInterest - selectedResult.totalInterest)
                        : "N/A"}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Debt Balance Over Time */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Debt Balance Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={selectedResult?.data || []}>
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(v) => `Mo ${v}`} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                        <Line 
                          type="monotone" 
                          dataKey="totalBalance" 
                          stroke="#10b981" 
                          strokeWidth={3}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Interest Comparison */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Interest Paid Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={comparisonData} layout="vertical">
                        <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                        <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="interest" radius={[0, 4, 4, 0]}>
                          {comparisonData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Debt Composition Pie */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Your Debt Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Payoff Order */}
            {selectedResult?.payoffOrder && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-500" />
                    Your Payoff Roadmap ({selectedStrategy === "avalanche" ? "Avalanche" : "Snowball"})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedResult.payoffOrder.map((debt, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl"
                      >
                        <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-navy-700">{debt.name}</p>
                          <p className="text-xs text-slate-500">
                            Paid off by month {debt.paidOffMonth} ({getDebtFreeDate(debt.paidOffMonth)})
                          </p>
                        </div>
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Educational Content */}
            <div className="grid md:grid-cols-2 gap-4">
              <Collapsible>
                <Card className="border-0 shadow-md">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-heading text-base flex items-center gap-2">
                        <Mountain className="w-5 h-5 text-emerald-500" />
                        What is Debt Avalanche?
                      </CardTitle>
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <p className="text-sm text-slate-600 mb-3">
                        Pay off debts starting with the <strong>highest interest rate</strong> first, 
                        while making minimum payments on others.
                      </p>
                      <div className="bg-emerald-50 p-3 rounded-lg">
                        <p className="text-xs text-emerald-700">
                          <strong>Best for:</strong> People who want to save the most money on interest. 
                          Mathematically optimal strategy.
                        </p>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              <Collapsible>
                <Card className="border-0 shadow-md">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="font-heading text-base flex items-center gap-2">
                        <Snowflake className="w-5 h-5 text-blue-500" />
                        What is Debt Snowball?
                      </CardTitle>
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <p className="text-sm text-slate-600 mb-3">
                        Pay off debts starting with the <strong>smallest balance</strong> first, 
                        regardless of interest rate.
                      </p>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-xs text-blue-700">
                          <strong>Best for:</strong> People who need quick wins and motivation. 
                          Builds momentum with early victories!
                        </p>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            </div>

            {/* Download CTA */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-navy-700 to-navy-800">
              <CardContent className="p-6 text-center">
                <h4 className="text-white font-heading font-bold text-lg mb-2">Save Your Debt-Free Plan</h4>
                <p className="text-slate-300 text-sm mb-4">
                  Download your personalized payoff schedule to track your progress
                </p>
                <Button 
                  onClick={handleDownloadPDF}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download My Plan
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MultiDebtCalculator;
